import { computed, DestroyRef, inject, Injectable, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { IJodit } from 'jodit/types/types';
import { debounceTime, Subject } from 'rxjs';

/**
 * JoditEditorStateService - Centralized state management for Jodit editor
 *
 * Manages all editor-related state including:
 *
 * - Editor instance and initialization state
 * - Content and character count
 * - Validation state
 * - Debounced change notifications
 *
 * Extracted from component to reduce file size and improve testability.
 */
@Injectable()
export class JoditEditorStateService {
  private readonly _destroyRef = inject(DestroyRef);

  // =========================================================================
  // Internal State Signals
  // =========================================================================

  private readonly _editor = signal<IJodit | null>(null);
  private readonly _editorReady = signal<boolean>(false);
  private readonly _internalValue = signal<string>('');
  private readonly _characterCount = signal<number>(0);
  private readonly _isInvalid = signal<boolean>(false);
  private readonly _contentChangeSubject = new Subject<void>();

  // =========================================================================
  // Public Read-only Signals
  // =========================================================================

  readonly editor: Signal<IJodit | null> = this._editor.asReadonly();
  readonly editorReady: Signal<boolean> = this._editorReady.asReadonly();
  readonly characterCount: Signal<number> = this._characterCount.asReadonly();
  readonly isInvalid: Signal<boolean> = this._isInvalid.asReadonly();
  readonly internalValue: Signal<string> = this._internalValue.asReadonly();

  // =========================================================================
  // Computed Signals
  // =========================================================================

  readonly isNearLimit = computed<(maxLength: number | undefined) => boolean>(() => {
    const count = this.characterCount();
    return (maxLength: number | undefined): boolean => (maxLength ? count / maxLength > 0.9 : false);
  });

  readonly isOverLimit = computed<(maxLength: number | undefined) => boolean>(() => {
    const count = this.characterCount();
    return (maxLength: number | undefined): boolean => (maxLength ? count > maxLength : false);
  });

  // =========================================================================
  // Public Methods
  // =========================================================================

  /** Set up debounced content change observer */
  setupContentChangeObserver(debounceMs: number, callback: () => void): void {
    this._contentChangeSubject.pipe(debounceTime(debounceMs), takeUntilDestroyed(this._destroyRef)).subscribe(callback);
  }

  /** Trigger a content change notification (will be debounced) */
  notifyContentChange(): void {
    this._contentChangeSubject.next();
  }

  /** Set the editor instance */
  setEditor(editor: IJodit | null): void {
    this._editor.set(editor);
  }

  /** Mark editor as ready */
  setEditorReady(ready: boolean): void {
    this._editorReady.set(ready);
  }

  /** Update internal value */
  setInternalValue(value: string): void {
    this._internalValue.set(value);
  }

  /** Update character count */
  setCharacterCount(count: number): void {
    this._characterCount.set(count);
  }

  /** Set validation state */
  setInvalid(invalid: boolean): void {
    this._isInvalid.set(invalid);
  }

  /** Update character count from editor */
  updateCharacterCountFromEditor(editor: IJodit): void {
    const text = editor.text || '';
    this.setCharacterCount(text.length);
  }

  /** Reset all state */
  reset(): void {
    this._editor.set(null);
    this._editorReady.set(false);
    this._internalValue.set('');
    this._characterCount.set(0);
    this._isInvalid.set(false);
  }
}
